import { useCallback, useMemo } from 'react';
import { useList } from 'react-use';

import { DemoContactWidget, DemoNoticeNotUsingRealData } from '@dreryk/demo-variant-frontend';
import { SessionRemainingTimeBanner } from '@dreryk/egabinet-security-frontend';

import { protectFeature } from '~libs.shared.system';
import { VideoConsultationWindow } from '~libs.desktop.video-consultation';
import { EmergencyNotificationAlertEventListener } from '~libs.desktop.emergency-notification';

import { ChangelogModal } from './components/ChangelogModal';
import { AccountConfigurationInfoModal } from './features/accountConfigurationInfo';
import { SubscriptionEndingModal } from './features/subscriptionEnding';
import { MfaRequiredModal } from './features/mfaRequired/MfaRequiredModal';

enum AppModal {
   CHANGELOG = 'CHANGELOG',
   ACCOUNT_CONFIGURATION = 'ACCOUNT_CONFIGURATION',
}

type UseModalsStackReturn = {
   handleOpenFactory: (modalName: AppModal) => () => void;
   handleCloseFactory: (modalName: AppModal) => () => void;
   canOpen: (modalName: AppModal) => boolean;
};

const useAppModalsStack = (): UseModalsStackReturn => {
   const [modalsStack, modalsStackMethods] = useList<AppModal>();
   const handleOpenFactory = useCallback((modalName: AppModal) => () => modalsStackMethods.push(modalName), [
      modalsStackMethods,
   ]);
   const handleCloseFactory = useCallback(
      (modalName: AppModal) => () => modalsStackMethods.filter((item) => modalName !== item),
      [modalsStackMethods]
   );
   return useMemo(
      () => ({
         handleOpenFactory: handleOpenFactory,
         handleCloseFactory: handleCloseFactory,
         canOpen: (modalName: AppModal) => modalsStack.length === 0 || modalName === modalsStack[0],
      }),
      [handleCloseFactory, handleOpenFactory, modalsStack]
   );
};

export const AppGlobalComponents = (): JSX.Element => {
   const { jitsiLink } = window.APP_ENV;
   // TODO DREGAB-1747 remove once this ticket is done
   const { handleCloseFactory, handleOpenFactory, canOpen } = useAppModalsStack();

   return (
      <>
         <ChangelogModal
            canOpen={canOpen(AppModal.CHANGELOG)}
            onOpen={handleOpenFactory(AppModal.CHANGELOG)}
            onClose={handleCloseFactory(AppModal.CHANGELOG)}
         />
         <AccountConfigurationInfoModal
            canOpen={canOpen(AppModal.ACCOUNT_CONFIGURATION)}
            onOpen={handleOpenFactory(AppModal.ACCOUNT_CONFIGURATION)}
            onClose={handleCloseFactory(AppModal.ACCOUNT_CONFIGURATION)}
         />
         <MfaRequiredModal />
         {protectFeature('updatingProviderData', <SubscriptionEndingModal />)}
         <DemoNoticeNotUsingRealData />
         <DemoContactWidget />
         <SessionRemainingTimeBanner />
         <EmergencyNotificationAlertEventListener />
         {protectFeature(
            'performingVideoConsultations',
            jitsiLink ? <VideoConsultationWindow domain={jitsiLink} /> : null,
            null
         )}
      </>
   );
};
